import { React, useState } from 'react';

import { FormHelperText } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';

import { Alerta } from '../../components/alerta';
import DayDatePicker from '../../components/dayDatePicker';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { formatDateLabelPtBr, formatIsoDate } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import { maskCpfCnpj } from '../../utils/maskCpfCnpj';
import { formatCurrency } from '../../utils/stringFormat';
import '../css/litigationPix.css';

const hasNumericValue = (value) => value !== '' && value !== null && !Number.isNaN(Number(value));

const GerarPixLitigio = () => {
    const [valorConta, setValorConta] = useState('');
    const [cpfCnpj, setCpfCnpj] = useState('');
    const [dateTime, setDateTime] = useState(null);
    const [error, setError] = useState(false);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    async function submitGerarPix (e) {
        e.preventDefault();
        setIsSubmit(true);

        let vencimento = '';
        if (dateTime) {
            setError(false);
            vencimento = formatIsoDate(dateTime);
        } else {
            setError(true);
            setIsSubmit(false);
            return;
        }

        const body = JSON.stringify({
            valor: valorConta,
            documento: cpfCnpj,
            vencimento
        });

        try {
            const response = await fetchFlask('gerarPixLitigio', body);
            setMessage(response.message);
            setStatus(response.status);
            setOpenAlerta(true);
            if (response.status === 'success') {
                setValorConta('');
                setCpfCnpj('');
            }
        } finally {
            setIsSubmit(false);
        }
    };

    const valueLabel = hasNumericValue(valorConta)
        ? formatCurrency(Number(valorConta))
        : 'Não informado';
    const dueDateLabel = formatDateLabelPtBr(dateTime);
    const completedFields = [
        hasNumericValue(valorConta),
        cpfCnpj,
        dateTime
    ].filter(Boolean).length;

    return (
        <Box className='mainBox litigation-pix-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>
                Gerar PIX Litígio
            </PageHeader>

            <form action='' method='POST' id='gerarPixLitigio' onSubmit={submitGerarPix} className='litigation-pix-layout'>
                <Box className='litigation-pix-workspace'>
                    <Box className='litigation-pix-intro'>
                        <Box>
                            <h2>PIX de litígio com conferência antes da emissão</h2>
                        </Box>
                        <p>
                            Informe valor, documento e vencimento para emitir o PIX com os dados revisados.
                        </p>
                    </Box>

                    <Box className='litigation-pix-panel'>
                        <Box className='litigation-pix-section-heading'>
                            <GavelRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Identificação do cliente</strong>
                            </Box>
                        </Box>

                        <Box className='litigation-pix-document-strip'>
                            <BadgeRoundedIcon />
                            <TextField
                                required
                                label='CPF/CNPJ do cliente'
                                value={cpfCnpj}
                                inputProps={{ maxLength: 18 }}
                                onChange={(e) => setCpfCnpj(maskCpfCnpj(e.target.value))}
                            />
                        </Box>
                    </Box>

                    <Box className='litigation-pix-panel'>
                        <Box className='litigation-pix-section-heading'>
                            <ReceiptLongRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Valor e vencimento</strong>
                            </Box>
                        </Box>

                        <Box className='litigation-pix-fields-grid'>
                            <TextField
                                required
                                label='Valor da parcela PIX'
                                type='number'
                                inputProps={{ step: 0.01, min: 0 }}
                                value={valorConta}
                                onChange={(e) => setValorConta(e.target.value)}
                            />

                            <FormControl className='litigation-pix-date-control' error={error}>
                                <DayDatePicker
                                    id='data'
                                    label='Vencimento'
                                    minDate={new Date('2025-09-01')}
                                    value={dateTime}
                                    onChange={setDateTime}
                                />
                                {error && (
                                    <FormHelperText>Por favor selecione uma data.</FormHelperText>
                                )}
                            </FormControl>
                        </Box>
                    </Box>

                    <Box className='litigation-pix-rail'>
                        <Box>
                            <PaymentsRoundedIcon />
                            <span>Destino financeiro</span>
                            <strong>PIX Litígio</strong>
                        </Box>
                        <Box>
                            <QrCode2RoundedIcon />
                            <span>Status</span>
                            <strong>Pronto para emissão</strong>
                        </Box>
                    </Box>
                </Box>

                <Box className='litigation-pix-summary'>
                    <Box className='litigation-pix-summary-header'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão do PIX</strong>
                        </Box>
                    </Box>

                    <Box className='litigation-pix-summary-list'>
                        <Box>
                            <span>Documento</span>
                            <strong className={!cpfCnpj ? 'is-empty' : undefined}>
                                {cpfCnpj || 'Não informado'}
                            </strong>
                        </Box>
                        <Box>
                            <span>Valor</span>
                            <strong>{valueLabel}</strong>
                        </Box>
                        <Box>
                            <span>Vencimento</span>
                            <strong className={!dateTime ? 'is-empty' : undefined}>{dueDateLabel}</strong>
                        </Box>
                    </Box>

                    <Box className='litigation-pix-attention'>
                        <AccountBalanceWalletRoundedIcon />
                        <p>
                            O PIX será gerado para o documento informado com o valor e vencimento revisados nesta tela.
                        </p>
                    </Box>

                    <Box className='litigation-pix-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/3 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<QrCode2RoundedIcon />}>
                        Gerar PIX
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default GerarPixLitigio;
